// src/components/agent/session/useAgentSession.js
//
// The heart of the agent page (frontend spec §8.4): owns the per-chat
// Firestore listeners (§6.1), the SSE overlay, the pending user message,
// the merge, turnActive (§6.5), and sendTurn (§7.1) with idempotent retry
// (§7.4), the queued release-window send (§6.5), stop and steer (§7.6),
// disconnect "following" mode (§7.5), and overlay retirement (§7.3).
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { collection, doc, query, orderBy, onSnapshot, setDoc, getDocs, limit, startAfter } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import {
  streamTurn,
  decideApproval,
  subscribePendingApprovals,
  fetchPendingApprovals,
  BusyError,
} from '../../../services/agentApi';
import { turnReducer } from './turnReducer';
import { loadFailureMessage } from '../../common/LoadFailure';
import { hydrateApprovalRecord, mergeApprovalRecord } from './approvalHydration';
import { loadMessageContent, subscribeMessageWindow } from './messageContent';
import {
  messageFromDoc,
  compareLedgerOrder,
  lastTurnCanvasPushes,
  totalTextLength,
  toMillis,
  inferUploadKind,
  deriveAutoName,
} from './partModel';

const MESSAGE_WINDOW = 100;               // newest messages kept live on open
const MESSAGE_PAGE = 50;                  // older messages fetched per scroll-back
const CLOCK_SKEW_MS = 30 * 1000;          // 30 s clock-skew slack on expires_at
// How long an in_progress message with no live lease still counts as a running
// turn. A turn claims its lease before it creates its message, so the only
// honest case is the moment a turn starts: the messages listener can deliver
// the new message before the agent-doc listener delivers the lease. That gap
// is seconds wide, and the window also absorbs clock skew between this browser
// and the server that stamped the message. After it, such a message belongs to
// a turn that failed, crashed or lost its lease, and the chat is free. It used
// to be the whole 3-minute lease time, which held back the very retry a failed
// turn asks the user to send. The two allowances add up: a browser clock that
// runs the full skew allowance ahead must still leave room for the listener gap.
const LISTENER_ORDER_GAP_MS = 30 * 1000;
const TURN_START_GRACE_MS = LISTENER_ORDER_GAP_MS + CLOCK_SKEW_MS;
const RELEASE_WINDOW_MS = 30 * 1000;      // post-`done` queued-send bound (§6.5)
const STEER_WINDOW_MS = 180 * 1000;       // steer-queued bound: the usual cycle tail (§7.6)
const EMPTY_META = {
  chatName: null,
  activeTurn: null,
  contextTokens: null,
  contextUpperLimit: null,
  exists: null,
};

export const isTurnLive = (activeTurn, now) =>
  activeTurn != null && toMillis(activeTurn.expires_at) > now - CLOCK_SKEW_MS;

// §7.3 render-source selection, verbatim.
const renderSource = (docMsg, overlay, streamAlive) => {
  if (!overlay || overlay.retired) return docMsg;
  if (docMsg == null) return overlay;
  if (docMsg.state !== 'in_progress') return docMsg; // terminal doc always wins — swap point
  if (streamAlive) return overlay;
  // Stream died: prefer the doc once it has caught up — compared by content
  // (total text), never part count, so text never regresses.
  return totalTextLength(docMsg.parts) >= totalTextLength(overlay.parts) ? docMsg : overlay;
};

const overlayAsViewMessage = (overlay) => ({
  id: overlay.id || 'overlay-draft',
  role: 'assistant',
  state: overlay.done ? 'complete' : 'in_progress',
  parts: overlay.parts,
  clientMessageId: null,
  timestampMs: 0,
  live: overlay.live,
});

// Optimistic user-message parts, in the order the backend contract pins for
// the persisted doc — canvas_edit, then text, then uploads/images (§4.4) —
// so the echo swap is visually invisible.
const buildPendingParts = (payload) => {
  const parts = [];
  if (payload.canvasEdit) {
    parts.push({
      kind: 'canvas_edit',
      source_filename: payload.canvasEdit.metadata?.source_filename,
      source_tool_use_id: payload.canvasEdit.metadata?.source_tool_use_id || '',
      edit_count: payload.canvasEdit.metadata?.edits?.length || 0,
    });
  }
  parts.push({ kind: 'user_text', text: payload.text || '' });
  (payload.files || []).forEach((file) => {
    const fileKind = inferUploadKind(file.name);
    if (fileKind === 'image' || fileKind === 'pdf') {
      parts.push({ kind: 'images', filename: file.name, fileKind, image_assets: [], total_page_count: 0 });
    } else {
      parts.push({ kind: 'upload', filename: file.name, fileKind });
    }
  });
  return parts;
};

/**
 * config:
 *   companyName          — effective company id
 *   agentId          — effective chat id (route param ?? draft ref), null for a fresh draft
 *   sessionEpoch         — bumped by "New chat" while already at /agent (§8.3)
 *   ensureAssistantId()  — returns { id, isNew }; mints the draft id into the page ref
 *   onChatCreated(id, autoName)   — at stream_start of a draft's first turn: navigate + name
 *   onCanvasFile(file)   — live canvas_file event → open the FilePane. The same
 *                          pushes are also derived from the ledger and returned
 *                          as canvasPushes, which is what a page that lost the
 *                          stream goes on: the page opens whichever reaches it
 *                          first and ignores the other.
 *   onSendComplete({ hadCanvasEdit }) — after a successful stream: canvas ops re-snapshot
 *   restoreComposer({ text, files })  — put a failed send's payload back in the composer
 *   holdQueuedSend(payload) — asked when a queued message is about to fire; true
 *                          means the page has taken the message over (it shows
 *                          the stale-workbook prompt) and it must not be sent
 *   onChatDeleted(id)    — assistant doc existed and now doesn't (deleted elsewhere)
 *   notify(message, options)          — snackbar ({ severity, retryable, onRetry })
 *                                        retryable: Retry re-sends the message as the page
 *                                        shows it now (retryLastSend(readCurrentPayload)),
 *                                        or the last chat message when called without one;
 *                                        onRetry: Retry runs this instead (an approval)
 */
// Whether a rebuilt payload is the failed one unchanged. The restore put the
// same File objects back, so identity is the right comparison for files; a
// canvas edit is re-exported on every build, so its edit log stands in for it.
const canvasEditLog = (payload) => JSON.stringify(payload?.canvasEdit?.metadata?.edits ?? null);
const samePayload = (current, failed) => (
  (current.text || '') === (failed.text || '')
  && (current.files || []).length === (failed.files || []).length
  && (current.files || []).every((file, index) => file === (failed.files || [])[index])
  && canvasEditLog(current) === canvasEditLog(failed)
);

export default function useAgentSession({
  companyName,
  agentId,
  isDraft,
  sessionEpoch,
  ensureAssistantId,
  onChatCreated,
  onCanvasFile,
  onSendComplete,
  restoreComposer,
  holdQueuedSend,
  onChatDeleted,
  notify,
}) {
  const [docMessages, setDocMessages] = useState([]);
  const [rawStates, setRawStates] = useState({}); // {id → state}, aborted docs included (§6.1)
  // History before the live window, oldest first: pages fetched as the user
  // scrolls back, plus messages the window let go as newer ones arrived. The
  // live-turn logic below only ever looks at the window (docMessages).
  const [olderMessages, setOlderMessages] = useState([]);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const loadingOlderRef = useRef(false);
  const windowDocsRef = useRef(new Map());     // id → { timestampMs, viewMessage } of the last snapshot
  const oldestLoadedSnapRef = useRef(null);    // cursor for the next older page
  const [assistantMeta, setAssistantMeta] = useState(EMPTY_META);
  const [overlay, setOverlay] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);
  const [queuedSend, setQueuedSend] = useState(null);
  const [streamState, setStreamState] = useState('idle'); // idle | streaming | following
  const [lastDoneAt, setLastDoneAt] = useState(null);
  const [stopRequestedFor, setStopRequestedFor] = useState(null); // message id (§7.6)
  const [timeTick, setTimeTick] = useState(0);
  // One-off external-effect approvals. Both maps are keyed by
  // approvalId = `${messageId}_${cycle}` (backend store contract).
  const [approvalDecisions, setApprovalDecisions] = useState({}); // → 'submitting'|'approved'|'denied'|'error'
  // One of the three listeners below failed. What they had delivered stays on
  // screen; a failed listener has stopped, so `retryListeners` resubscribes all
  // three (listenerAttempt is in the deps of the effects that start them).
  const [loadError, setLoadError] = useState(false);
  const [listenerAttempt, setListenerAttempt] = useState(0);
  const retryListeners = useCallback(() => {
    setLoadError(false);
    setListenerAttempt((n) => n + 1);
  }, []);
  // Hydrated durable approval records, fed by BOTH the live `approval_required`
  // event and the `pending_approvals` subscription. One entry per approval id,
  // so the same approval arriving from either source in either order renders as
  // exactly one card. Entries are retained after the record is resolved so the
  // conversation keeps a compact approved/denied outcome.
  const [approvals, setApprovals] = useState({});
  // The latest hydrated records, for a decision's failure path to consult
  // without re-subscribing.
  const approvalsRef = useRef(approvals);
  approvalsRef.current = approvals;
  // The request time, in milliseconds, from which this chat's approvals have
  // been loaded. Approval records are never deleted and each is large, so only
  // those of the messages on the page are read (bug 15.4): the subscription
  // starts at the oldest message of the first window, and each older page of
  // messages moves this back. Null means there is nothing older to load: the
  // approvals are held from the chat's beginning.
  const approvalsLoadedSinceMsRef = useRef(null);
  // Counts the messages listener's (re)subscriptions. Each one starts the
  // loaded history again from an empty list, so an older page still being
  // fetched under the listener before it belongs to a list that is gone.
  const messagesListenerGenerationRef = useRef(0);

  // Sends whose canvas edits have been acknowledged, by client_message_id. The
  // ledger echo and transport completion are two proofs of the same fact and
  // arrive in either order, so the second must not re-baseline the canvas: by
  // then the turn may be over, the pane unlocked, and a further edit made that
  // re-baselining would erase from the edit log without erasing it from the
  // workbook.
  const acknowledgedSendsRef = useRef(new Set());
  // The client_message_id of every message this page has sent in this chat.
  // The page uses it to tell its own canvas edit, which its open workbook
  // already contains, from a change made somewhere else.
  const ownSendIdsRef = useRef(new Set());
  const abortRef = useRef(null);
  // Approval resumes stream on their own connections, separate from the send in
  // abortRef, and more than one may be in flight (inflightApprovalsRef is a
  // Set) — so one controller each, all aborted together on chat switch/unmount.
  const resumeAbortsRef = useRef(new Set());
  const lastSendRef = useRef(null); // { payload, clientMessageId, busyCount }
  // Synchronous single-decision guard (survives the async setState round-trip
  // so a double-click cannot submit twice before React re-renders).
  const inflightApprovalsRef = useRef(new Set());
  const approvalDecisionsRef = useRef(approvalDecisions);
  approvalDecisionsRef.current = approvalDecisions;
  const decideRef = useRef(null); // latest decideApprovalDecision, for the merge's onDecide
  // Which chat is on screen, for a notification's Retry minted under an
  // earlier one: the snackbar outlives a chat switch, its action must not.
  const sessionKeyRef = useRef(null);
  sessionKeyRef.current = `${agentId}|${sessionEpoch}`;
  const configRef = useRef({});
  configRef.current = {
    ensureAssistantId, onChatCreated, onCanvasFile, onSendComplete, restoreComposer, holdQueuedSend, onChatDeleted, notify,
  };
  // Whether the assistant doc is known to exist (null while unresolved), read
  // at send time so a deep link to a missing chat is treated as a draft.
  const assistantExistsRef = useRef(null);
  assistantExistsRef.current = assistantMeta.exists;

  // ── Reset on chat switch (§8.3): non-null → different non-null, or → null.
  // The null → non-null transition (a draft becoming real at first send) must
  // NOT disturb the in-flight stream, so it is explicitly skipped.
  const prevIdRef = useRef(agentId);
  const prevEpochRef = useRef(sessionEpoch);
  useEffect(() => {
    const prevId = prevIdRef.current;
    const prevEpoch = prevEpochRef.current;
    prevIdRef.current = agentId;
    prevEpochRef.current = sessionEpoch;

    const epochChanged = prevEpoch !== sessionEpoch;
    const idChanged = prevId !== agentId && prevId != null;
    if (!epochChanged && !idChanged) return;

    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    resumeAbortsRef.current.forEach((controller) => controller.abort());
    resumeAbortsRef.current = new Set();
    lastSendRef.current = null;
    inflightApprovalsRef.current = new Set();
    acknowledgedSendsRef.current = new Set();
    ownSendIdsRef.current = new Set();
    setOverlay(null);
    setPendingUser(null);
    setQueuedSend(null);
    setStreamState('idle');
    setLastDoneAt(null);
    setStopRequestedFor(null);
    setApprovalDecisions({});
    setApprovals({});
  }, [agentId, sessionEpoch]);

  // Abort any live reader on unmount.
  useEffect(() => () => {
    if (abortRef.current) abortRef.current.abort();
    resumeAbortsRef.current.forEach((controller) => controller.abort());
  }, []);

  // Fold one hydrated record into the approvals map. Keying by approval id is
  // what deduplicates the same approval arriving live and durably, in either
  // order; the merge additionally refuses to regress an approval's lifecycle.
  const recordApproval = useCallback((model) => {
    if (!model) return;
    setApprovals((prev) => {
      const existing = prev[model.approvalId];
      const merged = mergeApprovalRecord(existing, model);
      return merged === existing ? prev : { ...prev, [model.approvalId]: merged };
    });
  }, []);

  // ── Listener 1: messages (§6.1.1) — synchronous callback, no GCS work.
  // Its first snapshot also starts listener 3, the durable parked approvals
  // (§7.13), because that snapshot is what says how far back the approvals of
  // the loaded messages go.
  useEffect(() => {
    setDocMessages([]);
    setRawStates({});
    setOlderMessages([]);
    setHasOlderMessages(false);
    setLoadingOlder(false);
    loadingOlderRef.current = false;
    windowDocsRef.current = new Map();
    oldestLoadedSnapRef.current = null;
    setApprovals({});
    approvalsLoadedSinceMsRef.current = null;
    messagesListenerGenerationRef.current += 1;
    if (!companyName || !agentId) return undefined;
    let unsubscribeApprovals = null;

    const messagesRef = collection(db, 'users', companyName, 'agents', agentId, 'messages');
    // Only the newest MESSAGE_WINDOW are live; the rest is paged in on demand.
    const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(MESSAGE_WINDOW));
    const unsubscribe = subscribeMessageWindow(messagesRef, q, (snapshot, bodies) => {
      const states = {};
      const mapped = [];
      const nextWindow = new Map();
      const docs = [...snapshot.docs].reverse(); // ledger order
      // A window that is not full holds the chat's whole ledger.
      const windowIsFull = docs.length === MESSAGE_WINDOW;
      docs.forEach((docSnap) => {
        const data = bodies.get(docSnap.id);
        states[docSnap.id] = data.state || 'complete';
        const viewMessage = messageFromDoc(data, docSnap.id);
        if (viewMessage) mapped.push(viewMessage);
        nextWindow.set(docSnap.id, { timestampMs: toMillis(data.timestamp), viewMessage });
      });

      // A bounded window slides: as messages arrive, the oldest leave the
      // snapshot without having been deleted. A message that was in the window
      // and is now older than everything in it moved off the back, and is kept
      // as history so the thread never shows a gap. Only a full window has a
      // back to fall off — a short one holds the whole ledger, so anything
      // missing from it was deleted and is dropped.
      const oldestInWindowMs = windowIsFull ? toMillis(docs[0].data().timestamp) : -Infinity;
      const fallenOut = [];
      windowDocsRef.current.forEach((entry, id) => {
        if (!nextWindow.has(id) && entry.timestampMs < oldestInWindowMs && entry.viewMessage) {
          fallenOut.push(entry.viewMessage);
        }
      });
      if (fallenOut.length) {
        fallenOut.sort((a, b) => a.timestampMs - b.timestampMs);
        setOlderMessages((prev) => [...prev, ...fallenOut]);
      }
      windowDocsRef.current = nextWindow;

      // The first snapshot fixes where older pages start; the window's later
      // oldest is always younger than that, with the fallen-out in between.
      if (!oldestLoadedSnapRef.current && snapshot.docs.length) {
        oldestLoadedSnapRef.current = snapshot.docs[snapshot.docs.length - 1];
        setHasOlderMessages(windowIsFull);
      }

      // ── Listener 3: durable parked approvals (§7.13). The card is rebuilt
      // from the persisted record, so a reconnect, a reload, or a second tab
      // shows the outstanding approval with exactly the detail the live event
      // carried. A message's approvals are requested after the message was
      // created, so the approvals of every message in a full window were
      // requested at or after the window's oldest message. The clock-skew
      // allowance covers the two times being stamped by different backend
      // instances. A window that is not full holds the whole chat, and so
      // does its subscription.
      //
      // An empty snapshot served from the local cache says nothing about the
      // chat's size: it is what Firestore delivers first when the browser is
      // offline. Subscribing on it would read every approval of the chat once
      // the connection returned, so the subscription waits for a snapshot
      // that has messages or comes from the server.
      const chatSizeUnknown = snapshot.metadata.fromCache && docs.length === 0;
      if (unsubscribeApprovals === null && !chatSizeUnknown) {
        const approvalsSinceMs = windowIsFull
          ? toMillis(docs[0].data().timestamp) - CLOCK_SKEW_MS
          : null;
        approvalsLoadedSinceMsRef.current = approvalsSinceMs;
        unsubscribeApprovals = subscribePendingApprovals(
          companyName,
          agentId,
          approvalsSinceMs === null ? null : new Date(approvalsSinceMs),
          (records) => records.forEach((record) => recordApproval(hydrateApprovalRecord(record))),
          () => setLoadError(true),
        );
      }
      setRawStates(states);
      setDocMessages(mapped);
    }, (error) => {
      console.error('Error listening to agent messages:', error);
      setLoadError(true);
    });
    return () => {
      unsubscribe();
      if (unsubscribeApprovals !== null) unsubscribeApprovals();
    };
  }, [companyName, agentId, sessionEpoch, listenerAttempt, recordApproval]);

  // ── Listener 2: assistant doc (§6.1.2) + deleted-elsewhere edge detection.
  useEffect(() => {
    setAssistantMeta(EMPTY_META);
    if (!companyName || !agentId) return undefined;

    let everExisted = false;
    const agentRef = doc(db, 'users', companyName, 'agents', agentId);
    const unsubscribe = onSnapshot(agentRef, { includeMetadataChanges: true }, (snap) => {
      if (!snap.exists()) {
        // New drafts have no head until their first claim. An existing chat
        // selected from stale history must be evicted even on its first read,
        // but a cache miss alone is not evidence of deletion.
        setAssistantMeta((prev) => ({ ...prev, exists: false }));
        if (!snap.metadata.fromCache && (everExisted || !isDraft)) {
          configRef.current.onChatDeleted?.(agentId);
        }
        return;
      }
      everExisted = true;
      const data = snap.data();
      if (data.deleting === true) {
        configRef.current.onChatDeleted?.(agentId, { deleting: true });
        return;
      }
      setAssistantMeta({
        chatName: data.chat_name || null,
        activeTurn: data.active_turn || null,
        contextTokens: data.context_tokens ?? null,
        contextUpperLimit: data.context_upper_limit ?? null,
        exists: true,
      });
    }, (error) => {
      console.error('Error listening to assistant doc:', error);
      setLoadError(true);
    });
    return unsubscribe;
  }, [companyName, agentId, isDraft, sessionEpoch, listenerAttempt]);

  // A send's canvas edit is acknowledged exactly once, by whichever proof that
  // it reached the backend arrives first.
  const acknowledgeSend = useCallback((clientMessageId, hadCanvasEdit) => {
    if (clientMessageId) {
      if (acknowledgedSendsRef.current.has(clientMessageId)) return;
      acknowledgedSendsRef.current.add(clientMessageId);
    }
    configRef.current.onSendComplete?.({ hadCanvasEdit });
  }, []);

  // ── Echo reconciliation (§7.1): exact client_message_id match, no signatures.
  useEffect(() => {
    if (!pendingUser) return;
    const persisted = docMessages.find(
      (m) => m.role === 'user' && m.clientMessageId && m.clientMessageId === pendingUser.clientMessageId
    );
    if (!persisted) return;
    // The message reaching the ledger is what proves a canvas edit landed, and
    // it is the only proof a page whose stream died mid-turn ever gets. Without
    // it the edit stays marked unsent and is silently attached to the next
    // message, which the backend then stages over the agent's newer workbook
    // (X13-10). The backend refuses the whole request if it cannot store the
    // edit, so a persisted message either carries the part or was sent with
    // none.
    if (persisted.parts.some((part) => part.kind === 'canvas_edit')) {
      acknowledgeSend(persisted.clientMessageId, true);
    }
    setPendingUser(null);
  }, [docMessages, pendingUser, acknowledgeSend]);

  // ── The workbooks the agent's last turn pushed to the canvas (§6.7). The
  // page opens the newest it has not opened yet, so a push the live stream
  // never delivered still reaches the pane.
  const canvasPushes = useMemo(() => lastTurnCanvasPushes(docMessages), [docMessages]);

  // ── Overlay retirement (§7.3): terminal doc, or aborted doc via the RAW
  // index (the render list hides aborted docs). `following` clears on ANY
  // retirement.
  useEffect(() => {
    if (!overlay || overlay.retired || !overlay.id) return;
    if (rawStates[overlay.id] === 'aborted') {
      setOverlay((prev) => (prev && !prev.retired ? { ...prev, retired: true, retiredBy: 'aborted' } : prev));
      setStreamState((prev) => (prev === 'following' ? 'idle' : prev));
      return;
    }
    const docMsg = docMessages.find((m) => m.id === overlay.id);
    if (docMsg && docMsg.state !== 'in_progress') {
      // The terminal doc already renders (renderSource picks it) — the
      // overlay object is simply discarded.
      setOverlay(null);
      setStreamState((prev) => (prev === 'following' ? 'idle' : prev));
    }
  }, [docMessages, rawStates, overlay]);

  const streamAlive = streamState === 'streaming';

  // ── turnActive (§6.5) — the ledger-derived busy signal.
  const turnActive = useMemo(() => {
    const now = Date.now();
    const leaseLive = isTurnLive(assistantMeta.activeTurn, now);
    const lastDoc = docMessages.length > 0 ? docMessages[docMessages.length - 1] : null;
    // Covers only the seconds-wide race where the messages listener delivers
    // an in_progress doc before the assistant-doc listener delivers
    // active_turn — time-bounded by the doc's own timestamp.
    const trailingLive = lastDoc?.state === 'in_progress' && lastDoc.timestampMs > now - TURN_START_GRACE_MS;
    return streamAlive || leaseLive || trailingLive;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantMeta.activeTurn, docMessages, streamAlive, timeTick]);

  // Post-`done` release window (§6.5): this tab just watched its own turn
  // finish; the backend still holds the lock through archive/suspend/release.
  const releaseWindow = useMemo(() => {
    if (!turnActive || streamAlive) return false;
    return lastDoneAt != null && Date.now() - lastDoneAt < RELEASE_WINDOW_MS;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnActive, streamAlive, lastDoneAt, timeTick]);

  // ── Stop (§7.6): the live assistant message id is known from stream_start
  // (own turn, before its doc is even visible) or from the trailing
  // non-`complete` assistant doc (another viewer's or headless turn) —
  // `in_progress` ordinarily, `awaiting_approval` during an approval resume,
  // which executes on the parked doc without flipping it back.
  const liveAssistantMessageId = useMemo(() => {
    // A response whose turn lost its lease is not the live turn: Stop must
    // reach whichever message the ledger shows as running now.
    if (overlay && !overlay.retired && !overlay.leaseLost && overlay.id) return overlay.id;
    for (let i = docMessages.length - 1; i >= 0; i -= 1) {
      const m = docMessages[i];
      if (m.role !== 'assistant') continue;
      return m.state === 'in_progress' || m.state === 'awaiting_approval' ? m.id : null;
    }
    return null;
  }, [overlay, docMessages]);

  // No stop during the release window (the turn already finished; the lock is
  // only draining archive/suspend) or after our own `done`.
  const canStop = turnActive
    && !releaseWindow
    && !(overlay && overlay.done)
    && liveAssistantMessageId != null;

  // Fire-and-forget: the backend honours the flag at the next cycle boundary
  // and the turn visibly ends when the terminal doc arrives. The write must
  // be exactly {interrupt: true} — a boolean — nothing server-side enforces
  // the shape and the strict backend parser rejects any other type.
  const stopTurn = useCallback(async () => {
    const target = liveAssistantMessageId;
    if (!companyName || !agentId || !target) return;
    setStopRequestedFor(target);
    try {
      await setDoc(
        doc(db, 'users', companyName, 'agents', agentId, 'messages', target),
        { interrupt: true },
        { merge: true }
      );
    } catch (error) {
      console.error('Error requesting stop:', error);
      setStopRequestedFor((current) => (current === target ? null : current));
      configRef.current.notify?.('Could not request a stop.', { severity: 'error' });
    }
  }, [companyName, agentId, liveAssistantMessageId]);

  // "stopping at the next step…" holds until the stopped turn's doc leaves
  // its live state (complete after the boundary commit), or the turn
  // evaporates without committing (crash — turnActive decays via the tick).
  const stopping = stopRequestedFor != null;
  useEffect(() => {
    if (!stopRequestedFor) return;
    const state = rawStates[stopRequestedFor];
    const stillLive = state === 'in_progress' || state === 'awaiting_approval';
    if ((state && !stillLive) || !turnActive) setStopRequestedFor(null);
  }, [rawStates, stopRequestedFor, turnActive]);

  // ── The ~30 s re-evaluation: nothing writes to Firestore after a crash, so
  // no snapshot forces a re-render — the interval keeps turnActive honest.
  const timeDependent =
    assistantMeta.activeTurn != null ||
    (docMessages.length > 0 && docMessages[docMessages.length - 1].state === 'in_progress');
  useEffect(() => {
    if (!timeDependent) return undefined;
    const interval = setInterval(() => setTimeTick((t) => t + 1), 30 * 1000);
    return () => clearInterval(interval);
  }, [timeDependent]);

  // The turn-start grace ends at a known moment, so re-evaluate exactly then
  // rather than up to 30 s later: a chat whose turn failed is freed on time,
  // and a message queued behind it fires as soon as it honestly can.
  useEffect(() => {
    const lastDoc = docMessages.length > 0 ? docMessages[docMessages.length - 1] : null;
    if (!lastDoc || lastDoc.state !== 'in_progress') return undefined;
    const remaining = lastDoc.timestampMs + TURN_START_GRACE_MS - Date.now();
    if (remaining <= 0) return undefined;
    const timer = setTimeout(() => setTimeTick((t) => t + 1), remaining + 50);
    return () => clearTimeout(timer);
  }, [docMessages]);

  useEffect(() => {
    if (streamState !== 'following') return;
    // The banner clears on overlay retirement (§7.3, handled above) and it
    // must never outlive the turn it describes (§7.5): a crashed turn whose
    // lease has expired drops turnActive via the interval re-evaluation.
    if (!turnActive) {
      setStreamState('idle');
      // A pending echo still unmatched when following ends means our send
      // never actually landed: the user doc is durably appended before any
      // stream events, so a genuinely-running turn always clears the echo by
      // id match. This is the 409-on-retry false positive (another turn held
      // the lock) — self-heal: drop the ghost bubble and hand the message back.
      setPendingUser((current) => {
        if (!current) return current;
        const last = lastSendRef.current;
        if (last && last.clientMessageId === current.clientMessageId) {
          configRef.current.restoreComposer?.({ text: last.payload.text, files: last.payload.files });
          configRef.current.notify?.(
            'Your message was not sent — the agent was busy with another task.',
            { severity: 'warning', retryable: true }
          );
        }
        return null;
      });
    }
  }, [streamState, turnActive]);

  // ── Context meter: read from the agent document alone. Every commit and
  // every approval park writes context_tokens there, and the listener
  // delivers it in the same moment as the stream's `done`. The stream's own
  // context_pct is ignored: the approval and failure frames carried none
  // worth reading, and a stale stream value could outlive the document's
  // (B9-7).
  const contextPct = useMemo(() => {
    if (assistantMeta.contextTokens == null || !assistantMeta.contextUpperLimit) return null;
    return Math.round((assistantMeta.contextTokens / assistantMeta.contextUpperLimit) * 1000) / 10;
  }, [assistantMeta.contextTokens, assistantMeta.contextUpperLimit]);

  // ── The merge (§8.4): docs ⊕ pending user ⊕ overlay, with §6.2 decorations.
  // One page of history before the oldest message loaded so far. A chat switch
  // mid-fetch drops the result: the listener reset above already cleared
  // everything this would have joined.
  const loadOlderMessages = useCallback(async () => {
    const cursor = oldestLoadedSnapRef.current;
    if (!cursor || loadingOlderRef.current || !companyName || !agentId) return;
    const sessionKey = sessionKeyRef.current;
    const listenerGeneration = messagesListenerGenerationRef.current;
    // Whether the list this page was fetched for is gone: another chat is on
    // screen, or the messages listener was restarted (a retry after a listener
    // failure), which starts the loaded history, its cursor and its approvals
    // again. A page kept after that would leave a gap in both.
    const overtaken = () => (
      sessionKeyRef.current !== sessionKey
      || messagesListenerGenerationRef.current !== listenerGeneration
    );
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    try {
      const messagesRef = collection(db, 'users', companyName, 'agents', agentId, 'messages');
      const snapshot = await getDocs(
        query(messagesRef, orderBy('timestamp', 'desc'), startAfter(cursor), limit(MESSAGE_PAGE))
      );
      if (overtaken()) return;
      const page = [];
      for (const docSnap of [...snapshot.docs].reverse()) {
        const body = await loadMessageContent(docSnap);
        if (body) {
          const message = messageFromDoc(body, docSnap.id);
          if (message) page.push(message);
        }
      }
      if (overtaken()) return;

      // The approvals of this page's messages: those requested from the page's
      // oldest message up to where the approvals loaded so far begin. A short
      // page is the chat's beginning, so it has no lower bound. They are read
      // before anything is kept: a failure fails the whole page, and the retry
      // loads the messages and their approval cards together.
      const reachedBeginning = snapshot.docs.length < MESSAGE_PAGE;
      const pageApprovalsSinceMs = reachedBeginning
        ? null
        : toMillis(snapshot.docs[snapshot.docs.length - 1].data().timestamp) - CLOCK_SKEW_MS;
      const approvalsLoadedSinceMs = approvalsLoadedSinceMsRef.current;
      let pageApprovals = [];
      if (approvalsLoadedSinceMs !== null) {
        pageApprovals = await fetchPendingApprovals(
          companyName,
          agentId,
          pageApprovalsSinceMs === null ? null : new Date(pageApprovalsSinceMs),
          new Date(approvalsLoadedSinceMs),
        );
      }
      if (overtaken()) return;

      if (snapshot.docs.length) oldestLoadedSnapRef.current = snapshot.docs[snapshot.docs.length - 1];
      approvalsLoadedSinceMsRef.current = pageApprovalsSinceMs;
      pageApprovals.forEach((record) => recordApproval(hydrateApprovalRecord(record)));
      setOlderMessages((prev) => [...page, ...prev]);
      setHasOlderMessages(snapshot.docs.length === MESSAGE_PAGE);
    } catch (error) {
      console.error('Error loading older messages:', error);
      if (!overtaken()) {
        configRef.current.notify?.(loadFailureMessage('older messages'), { severity: 'error' });
      }
    } finally {
      // A restarted listener has already reset these for its own list.
      if (!overtaken()) {
        loadingOlderRef.current = false;
        setLoadingOlder(false);
      }
    }
  }, [companyName, agentId, recordApproval]);

  const messages = useMemo(() => {
    const now = Date.now();
    const leaseLive = isTurnLive(assistantMeta.activeTurn, now);
    const overlayActive = overlay && !overlay.retired;

    const decorateAssistant = (m) => {
      if (m.role !== 'assistant') return m;
      if (m.state === 'in_progress') {
        const isOwnOverlay = overlayActive && overlay.id === m.id;
        // Our own turn's `done` means the commit already happened — the
        // terminal doc is moments away; show no indicator in the gap.
        if (isOwnOverlay && overlay.done) return m;
        // The backend said our turn lost its lease and stopped. A lease that
        // is live now belongs to another turn, so it must not make this
        // message look as if it were still being written.
        if (isOwnOverlay && overlay.leaseLost) return { ...m, interruptedTail: true };
        // A live turn behind it: the local stream owns it, the lease holds,
        // or the doc is still inside the turn-start grace (§6.2).
        const liveBehind = (isOwnOverlay && streamAlive)
          || leaseLive
          || m.timestampMs > now - TURN_START_GRACE_MS;
        return liveBehind
          ? { ...m, working: true }
          : { ...m, interruptedTail: true }; // never thinking dots on a dead turn
      }
      if (m.state === 'awaiting_approval') return { ...m, paused: true };
      return m;
    };

    // Overlay-sourced messages carry live extras (cursor, thinking) only
    // while the stream is actually alive; afterwards (following / a crashed
    // turn) they degrade through the same §6.2 liveness rules as doc-sourced
    // in_progress messages — never a blinking cursor on a dead stream.
    const overlayView = (docMsg) => {
      const vm = overlayAsViewMessage(overlay);
      if (overlay.leaseLost) {
        // The backend said this turn stopped without committing. Whatever
        // holds the lease now is another turn, not this response.
        vm.live = null;
        vm.interruptedTail = true;
        return vm;
      }
      if (overlay.done || streamAlive) return vm;
      vm.live = null;
      const liveBehind = leaseLive
        || (docMsg ? docMsg.timestampMs > now - TURN_START_GRACE_MS : false);
      if (liveBehind) vm.working = true;
      else vm.interruptedTail = true;
      return vm;
    };

    const out = [];
    let overlayInList = false;
    // History first, then the live window, in ledger order (the window and pages are
    // fetched by timestamp; a compaction divider's timestamp postdates the user
    // message it precedes); the overlay can only be in the window.
    for (const m of [...olderMessages, ...docMessages].sort(compareLedgerOrder)) {
      if (overlayActive && m.role === 'assistant' && m.id === overlay.id) {
        overlayInList = true;
        const source = renderSource(m, overlay, streamAlive);
        out.push(source === overlay ? overlayView(m) : decorateAssistant(m));
        continue;
      }
      out.push(decorateAssistant(m));
    }

    // Pending user sits before the floating overlay.
    if (pendingUser) {
      const persisted = docMessages.some(
        (m) => m.role === 'user' && m.clientMessageId && m.clientMessageId === pendingUser.clientMessageId
      );
      if (!persisted) out.push(pendingUser);
    }

    if (overlayActive && !overlayInList) {
      out.push(overlayView(null));
    }

    // Retirement by abort renders a single interrupted notice in the
    // overlay's place (§7.3).
    if (overlay && overlay.retired && overlay.retiredBy === 'aborted') {
      out.push({
        id: `interrupted-${overlay.id}`,
        role: 'system',
        state: 'complete',
        parts: [],
        interrupted: true,
        clientMessageId: null,
        timestampMs: 0,
        live: null,
      });
    }

    // Attach each hydrated approval to the message whose cycle it parked, and
    // decorate it with the live decision state and a bound decision handler.
    // The approvals map is already deduplicated by approval id, so exactly one
    // card renders however many times the record arrived.
    const approvalModels = Object.values(approvals);
    const attachApprovals = (m) => {
      if (m.role !== 'assistant') return m;
      const mine = approvalModels
        .filter((model) => model.messageId === m.id)
        .sort((a, b) => a.cycle - b.cycle);
      if (mine.length === 0) return m;
      const parts = [...m.parts];
      // Anchor each card to the tool call that parked it, so it stays put in
      // the thread while the resumed turn streams in below. Live parts carry
      // no cycle — toolUseId is the identity both live and persisted parts
      // share — so anchoring on the call is what keeps this stable.
      const insertApproval = (decorated, model) => {
        const anchors = new Set((model.askCalls || []).map((c) => c.toolUseId));
        // Directly after the parked call's chip. Its table/chart/file outputs
        // share the same toolUseId, so anchoring on the chip specifically is
        // what keeps the card above them.
        const anchor = parts.findIndex(
          (p) => p.kind === 'tool_call' && p.toolUseId && anchors.has(p.toolUseId)
        );
        if (anchor === -1) parts.push(decorated);
        else parts.splice(anchor + 1, 0, decorated);
      };
      mine.forEach((model) => {
        // Once the backend has started executing the decision (or completed
        // or closed the approval) the durable record's outcome is the truth,
        // whatever this tab's last request did. Until then this tab's own
        // state comes first: a decision the backend recorded but could not
        // resume yet (the chat was still busy archiving) must stay retryable
        // rather than collapse as done (B9-3).
        const local = approvalDecisions[model.approvalId];
        const executionStarted = model.state === 'executing' || model.state === 'completed';
        const decisionStatus = executionStarted && model.decisionOutcome
          ? model.decisionOutcome
          : (local || model.decisionOutcome || 'pending');
        insertApproval({
          ...model,
          decisionStatus,
          onDecide: (decision) => decideRef.current?.(model, decision),
        }, model);
      });
      return { ...m, parts };
    };

    return out.map(attachApprovals);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [olderMessages, docMessages, overlay, pendingUser, streamAlive, assistantMeta.activeTurn, timeTick, approvalDecisions, approvals]);

  // ── The send machinery ─────────────────────────────────────────────────

  // A live `approval_required` event carries the same persisted record the
  // durable subscription delivers, so it goes through the same hydrator. The
  // event is only ever the fastest arrival — never a separate source of truth.
  const captureApproval = useCallback((eventData) => {
    if (!eventData || eventData.type !== 'approval_required') return;
    recordApproval(hydrateApprovalRecord(eventData));
  }, [recordApproval]);

  const rollbackOptimistic = useCallback(() => {
    setOverlay(null);
    setPendingUser(null);
  }, []);

  const doSend = useCallback(async (payload, clientMessageId, { isRetry = false, autoRetried = false } = {}) => {
    const cfg = configRef.current;
    const { id: targetAssistantId, isNew: minted } = cfg.ensureAssistantId();
    if (!targetAssistantId) return { status: 'failed' };
    // A route id whose doc is known to be missing (a stale or hand-typed deep
    // link) is a draft too: the backend creates the canonical doc on this
    // send, and the chat gets auto-named like any other first message.
    const isNew = minted || assistantExistsRef.current === false;

    lastSendRef.current = lastSendRef.current?.clientMessageId === clientMessageId
      ? lastSendRef.current
      : { payload, clientMessageId, busyCount: 0 };
    ownSendIdsRef.current.add(clientMessageId);

    // Optimistic UI (§7.1 step 3) — this setState also forces the render that
    // carries a freshly minted draft id into the tree (mint-then-set ordering).
    setPendingUser({
      id: `pending-${clientMessageId}`,
      role: 'user',
      state: 'complete',
      clientMessageId,
      parts: buildPendingParts(payload),
      timestampMs: Date.now(),
      live: null,
    });
    setOverlay({
      id: null,
      parts: [],
      live: { streaming: true, thinking: false, thinkingText: '', compacting: false },
      retired: false,
      retiredBy: null,
      done: false,
      leaseLost: false,
    });
    setStreamState('streaming');
    setLastDoneAt(null);

    const controller = new AbortController();
    abortRef.current = controller;
    let gotEvents = false;
    // `done` is the backend's promise that the turn was committed. A stream
    // that ends without it has not finished the turn: the viewer fell too far
    // behind and was cut loose while the turn runs on, or the turn lost its
    // lease and stopped.
    let sawDone = false;
    // `not_saved` is the backend saying the turn failed before anything of
    // this message reached the ledger, and that the message may be sent again
    // at once.
    let sawNotSaved = false;

    const handleEvent = (eventData) => {
      gotEvents = true;
      if (eventData.type === 'done') sawDone = true;
      if (eventData.type === 'not_saved') sawNotSaved = true;
      if (eventData.type === 'stream_start' && isNew) {
        cfg.onChatCreated?.(targetAssistantId, deriveAutoName(payload.text, payload.files));
      }
      if (eventData.type === 'canvas_file') {
        cfg.onCanvasFile?.({
          url: eventData.download_url,
          gcsPath: eventData.gcs_path || null,
          filename: eventData.filename,
          contentType: eventData.content_type,
          description: eventData.description,
          toolUseId: eventData.tool_use_id,
          sandboxPath: eventData.sandbox_path || null,
        });
        return;
      }
      if (eventData.type === 'done') setLastDoneAt(Date.now());
      captureApproval(eventData);
      setOverlay((prev) => turnReducer(prev, eventData));
    };

    // `not_saved`: nothing of this message was saved, so it was not sent. It is
    // handed back exactly as a send that never reached the backend is: the
    // unsaved bubble goes, the text and attachments return to the composer,
    // and the canvas edit stays unacknowledged so it goes out with the next
    // send. `lastSendRef` is kept, so Retry repeats this message with the
    // same id.
    const handBackUnsavedMessage = () => {
      rollbackOptimistic();
      setStreamState('idle');
      cfg.restoreComposer?.({ text: payload.text, files: payload.files });
      cfg.notify?.('Sorry, the message could not be sent.', { severity: 'error', retryable: true });
      return { status: 'failed' };
    };

    try {
      const result = await streamTurn({
        agentId: targetAssistantId,
        userMessage: payload.text || '',
        files: payload.files || [],
        canvasEdit: payload.canvasEdit || null,
        clientMessageId,
        signal: controller.signal,
        onEvent: handleEvent,
      });

      if (result.outcome === 'aborted') {
        // Deliberate abort (chat switch, unmount): clean no-op — the switch
        // handler already cleared overlay/pending (§8.1).
        return { status: 'aborted' };
      }

      if (abortRef.current === controller) abortRef.current = null;

      if (sawNotSaved) return handBackUnsavedMessage();

      if (result.outcome === 'streamed' && !sawDone) {
        // Exactly the stream-death case below (§7.5), except that the
        // connection closed cleanly: keep what was streamed and follow the
        // turn through the message listener until the ledger says it is over.
        setStreamState('following');
        return { status: 'following' };
      }

      setStreamState('idle');

      if (result.outcome === 'replayed') {
        // The turn already completed on an earlier attempt — drop the overlay
        // and let the listener render the finished turn (§7.4).
        setOverlay(null);
        acknowledgeSend(clientMessageId, !!payload.canvasEdit);
        lastSendRef.current = null;
        return { status: 'sent' };
      }

      // outcome === 'streamed': retirement (§7.3) handles the overlay. The echo
      // reconciliation above ordinarily acknowledged the canvas edit long
      // before this; this is what still acknowledges one when the messages
      // listener is down.
      acknowledgeSend(clientMessageId, !!payload.canvasEdit);
      lastSendRef.current = null;
      return { status: 'sent' };
    } catch (error) {
      if (controller.signal.aborted) return { status: 'aborted' };
      if (abortRef.current === controller) abortRef.current = null;

      if (error instanceof BusyError) {
        const busyCountBefore = lastSendRef.current?.clientMessageId === clientMessageId
          ? lastSendRef.current.busyCount
          : 0;
        if (lastSendRef.current?.clientMessageId === clientMessageId) {
          lastSendRef.current.busyCount += 1;
        }
        if (isRetry && busyCountBefore === 0) {
          // 409-in-flight on a retry of an AMBIGUOUS failure (network-level):
          // the first attempt actually started the turn — success-in-progress,
          // never an error (§7.4). Keep the pending echo; the doc listener
          // renders the running turn. (A retry after a CLEAN busy — the lock
          // claim was denied, so no reservation survived — falls through to
          // the ordinary busy path and accumulates toward the fresh-id rule.)
          setOverlay(null);
          setStreamState('following');
          return { status: 'following' };
        }
        rollbackOptimistic();
        setStreamState('idle');
        cfg.restoreComposer?.({ text: payload.text, files: payload.files });
        cfg.notify?.(
          `The agent is already working on this chat — try again${error.retryAfter ? ` in ~${error.retryAfter}s` : ' shortly'}.`,
          { severity: 'warning', retryable: true }
        );
        return { status: 'busy' };
      }

      // The connection can still break after the `not_saved` frame arrived.
      // The backend's statement stands: there is no turn to follow, and no
      // ambiguity for an automatic retry to resolve.
      if (sawNotSaved) return handBackUnsavedMessage();

      if (gotEvents) {
        // Stream death mid-turn (§7.5): the turn is NOT dead — v2 finishes it
        // headless. Keep rendering; the doc listener continues block-level
        // hydration. No fake error, no retry POST.
        setStreamState('following');
        return { status: 'following' };
      }

      // Setup failure before any event (§7.3). Network-level connection
      // failures get one automatic retry with the same client_message_id —
      // the recursion re-establishes the same optimistic state, so no
      // rollback (and no flicker) in between.
      const isNetworkError = error instanceof TypeError;
      if (isNetworkError && !autoRetried) {
        return doSend(payload, clientMessageId, { isRetry: true, autoRetried: true });
      }

      console.error('Error sending agent turn:', error);
      rollbackOptimistic();
      setStreamState('idle');
      cfg.restoreComposer?.({ text: payload.text, files: payload.files });
      cfg.notify?.(
        `Sorry, the message could not be sent${error.message ? ` (${error.message})` : ''}.`,
        { severity: 'error', retryable: true }
      );
      return { status: 'failed' };
    }
  }, [rollbackOptimistic, captureApproval, acknowledgeSend]);

  /**
   * §7.1 end-to-end. payload: { text, files, canvasEdit } where canvasEdit =
   * { blob, filename, metadata } | null. All outcomes handled internally.
   */
  const sendTurn = useCallback(async (payload) => {
    const hasContent = !!(payload.text?.trim() || payload.files?.length || payload.canvasEdit);
    if (!hasContent) return { status: 'noop' };

    // The Composer clears its field before calling onSend, so every path that
    // refuses the send must hand the payload back — losing typed content in
    // the render-to-click race would be silent data loss.
    if (turnActive || streamAlive) {
      if (releaseWindow) {
        // Queue through the backend's post-`done` release window; fires when
        // active_turn clears (§6.5). The turn already finished — no stop.
        setQueuedSend({
          payload,
          clientMessageId: crypto.randomUUID(),
          queuedAt: Date.now(),
          boundMs: RELEASE_WINDOW_MS,
          sessionKey: sessionKeyRef.current,
        });
        return { status: 'queued' };
      }
      // Steer (§7.6): stop the running turn at its next boundary and hold the
      // message in the same queued-send, firing as an ordinary next turn when
      // active_turn clears. The wider bound covers the usual cycle tail — the
      // in-flight model call, a tool at the backend's default timeout of two
      // minutes, and the end-of-turn archive. It does not cover every tool:
      // the backend allows Python and shell commands five minutes and a
      // BigQuery read ten, and a message queued behind one of those is handed
      // back to the composer when the bound passes.
      stopTurn();
      setQueuedSend({
        payload,
        clientMessageId: crypto.randomUUID(),
        queuedAt: Date.now(),
        boundMs: STEER_WINDOW_MS,
        sessionKey: sessionKeyRef.current,
      });
      return { status: 'queued' };
    }

    return doSend(payload, crypto.randomUUID(), {});
  }, [streamAlive, turnActive, releaseWindow, stopTurn, doSend]);

  // Fire the queued send the moment active_turn clears; degrade to the busy
  // snackbar with the message restored after the queue's own bound — 30 s for
  // the release window, 180 s for a steer (§6.5, §7.6).
  useEffect(() => {
    if (!queuedSend) return undefined;
    if (!turnActive) {
      const { payload, clientMessageId, sessionKey } = queuedSend;
      setQueuedSend(null);
      // A chat switch clears the queue, but when the old chat's turn ends in
      // the same render as the switch, this effect still runs once holding
      // the message of the chat that was left. Sending it now would post it
      // into the chat on screen, so it is dropped, exactly as the reset on a
      // chat switch drops a queued message in every other case.
      if (sessionKeyRef.current !== sessionKey) return undefined;
      // The message was built when Send was pressed, up to three minutes ago,
      // and the turn it waited behind may have changed the workbook its
      // canvas edits were made on. The page checks again now and may keep the
      // message for its stale-workbook prompt instead of letting it send.
      if (configRef.current.holdQueuedSend?.(payload)) return undefined;
      doSend(payload, clientMessageId, {});
      return undefined;
    }
    const bound = queuedSend.boundMs ?? RELEASE_WINDOW_MS;
    const remaining = Math.max(0, queuedSend.queuedAt + bound - Date.now());
    const timer = setTimeout(() => {
      setQueuedSend((current) => {
        if (!current) return current;
        configRef.current.restoreComposer?.({ text: current.payload.text, files: current.payload.files });
        configRef.current.notify?.('The agent is still busy — your message was not sent.', { severity: 'warning' });
        return null;
      });
    }, remaining);
    return () => clearTimeout(timer);
  }, [queuedSend, turnActive, doSend]);

  // §7.4: reuse the id on Retry; after two consecutive busies, mint a fresh
  // one (by then the original turn either never started or is visible).
  //
  // `readCurrentPayload` rebuilds the message as the page shows it when Retry
  // is pressed — the composer's draft (the failed send was handed back to it,
  // and the person may since have removed a rejected attachment or edited the
  // text) with the canvas edits as they stand now (M225). It is only called
  // once a failed send is known to exist, because reading the draft clears
  // the composer. A payload that differs from the failed one is a new
  // message, so it takes a fresh id rather than replaying the failed request
  // under its idempotency key; an unchanged one keeps the same-id retry that
  // tells an ambiguous failure apart from a turn that actually started.
  // Without a reader the failed payload is re-sent as before.
  const retryLastSend = useCallback(async (readCurrentPayload = null) => {
    const last = lastSendRef.current;
    if (!last) return;
    let payload = last.payload;
    if (readCurrentPayload) {
      payload = await readCurrentPayload();
      if (!payload) return; // Nothing to read, or the message was parked instead.
      if (!payload.text?.trim() && !(payload.files?.length > 0) && !payload.canvasEdit) {
        return; // The person cleared the draft: nothing to send.
      }
    }
    const edited = readCurrentPayload && !samePayload(payload, last.payload);
    // Unchanged: replay the failed payload itself, so a re-exported canvas
    // workbook cannot alter the bytes behind the request's content hash.
    if (!edited) payload = last.payload;
    if (edited || last.busyCount >= 2) {
      const freshId = crypto.randomUUID();
      lastSendRef.current = { payload, clientMessageId: freshId, busyCount: 0 };
      doSend(payload, freshId, {});
      return;
    }
    doSend(payload, last.clientMessageId, { isRetry: true });
  }, [doSend]);

  // ── Approvals (one-off) ─────────────────────────────────────────────────
  // Submit an approve/deny decision for a parked cycle and reflect the outcome.
  // The final decision resumes the parked cycle over a fresh SSE stream; the
  // message-doc listener then renders the continuation (approved) or the
  // model-readable refusal (denied), exactly like the disconnect-follow path.
  const decideApprovalDecision = useCallback(async (approval, decision) => {
    const cfg = configRef.current;
    if (!approval) return;
    const { approvalId, messageId, cycle, decisionIds } = approval;
    if (!companyName || !agentId || !approvalId || !messageId || cycle == null) return;
    if (decision !== 'approved' && decision !== 'denied') return;

    // Exactly one outstanding decision per approval: refuse a double-click, a
    // decision already in flight, or re-deciding an already-terminal approval.
    // The synchronous ref guard holds even before React re-renders, and it is
    // also what keeps a reconnected/replayed approval single.
    if (inflightApprovalsRef.current.has(approvalId)) return;
    const settled = approvalDecisionsRef.current[approvalId];
    if (settled === 'approved' || settled === 'denied') return;

    // The hydrator's decidable set: every ASK call in the record and nothing
    // else. It is empty for a non-approvable record, which refuses the decision
    // here rather than approving a partially understood cycle.
    const decisions = {};
    (decisionIds || []).forEach((toolUseId) => { decisions[toolUseId] = decision; });
    if (Object.keys(decisions).length === 0) return;

    inflightApprovalsRef.current.add(approvalId);
    setApprovalDecisions((prev) => ({ ...prev, [approvalId]: 'submitting' }));

    // The resumed turn streams on its own connection. Without this signal a
    // chat switch left it running, and its events — a canvas file, the
    // context percentage, a follow-on approval — landed in the chat now shown.
    const controller = new AbortController();
    resumeAbortsRef.current.add(controller);
    let gotEvents = false;
    let sawDone = false;
    let sawNotSaved = false;

    const handleResumeEvent = (eventData) => {
      // A batch parsed just before the abort must not land in the next chat.
      if (controller.signal.aborted) return;
      gotEvents = true;
      if (eventData.type === 'done') sawDone = true;
      if (eventData.type === 'not_saved') sawNotSaved = true;
      if (eventData.type === 'canvas_file') {
        cfg.onCanvasFile?.({
          url: eventData.download_url,
          gcsPath: eventData.gcs_path || null,
          filename: eventData.filename,
          contentType: eventData.content_type,
          description: eventData.description,
          toolUseId: eventData.tool_use_id,
          sandboxPath: eventData.sandbox_path || null,
        });
        return;
      }
      if (eventData.type === 'done') setLastDoneAt(Date.now());
      // The resumed turn may itself re-park on a further external effect.
      captureApproval(eventData);
    };

    // `not_saved`: the decision itself is recorded, but the resumed turn
    // failed before it saved anything, so the decided action never started.
    // The backend accepts the same decision again and resumes at once. Retry
    // repeats THIS decision, and only while its chat is still on screen,
    // exactly as the retry below for a decision that could not be submitted.
    const reportResumeNotStarted = () => {
      setApprovalDecisions((prev) => ({ ...prev, [approvalId]: 'error' }));
      const sessionKey = sessionKeyRef.current;
      cfg.notify?.('Your decision was saved, but the agent could not continue.', {
        severity: 'error',
        onRetry: () => {
          if (sessionKeyRef.current !== sessionKey) return;
          decideRef.current?.(approval, decision);
        },
      });
    };

    try {
      const result = await decideApproval({
        agentId, messageId, cycle, decisions, signal: controller.signal, onEvent: handleResumeEvent,
      });
      // Deliberate abort (chat switch, unmount): the reset effect already
      // cleared this session's state, and this decision belongs to the old chat.
      if (result?.outcome === 'aborted') return;
      // A chat switch aborts this controller, but aborting a request that has
      // already settled does nothing, so the outcome above still reads
      // 'streamed'. Everything below belongs to the chat that was left.
      if (controller.signal.aborted) return;
      if (sawNotSaved) {
        reportResumeNotStarted();
        return;
      }
      setApprovalDecisions((prev) => ({ ...prev, [approvalId]: decision }));
      // A resumed turn's stream that ends without `done` means the same as it
      // does for a send: the turn is not finished, so follow it.
      if (result?.outcome === 'streamed' && !sawDone) setStreamState('following');
    } catch (error) {
      if (controller.signal.aborted) return;
      // The connection can still break after the `not_saved` frame arrived.
      if (sawNotSaved) {
        reportResumeNotStarted();
        return;
      }
      // The backend records the decision before it starts the resumed turn,
      // so a failure means "not submitted" only when nothing shows the resume
      // started. A resumed-turn event shows it: the turn carries on headless
      // and the page follows it through the document listener, as for a send
      // whose stream died. So does the durable record having moved to
      // executing or completed. Reporting "could not submit" in either case
      // would invite a second, contradicting decision (B9-3). A record still
      // at decisions_recorded means the decision was saved but the resume
      // was refused (the chat was busy): the retry below resubmits the same
      // decision, which the backend accepts and resumes once the chat is free.
      const record = approvalsRef.current[approvalId];
      const executionStarted = record?.state === 'executing' || record?.state === 'completed';
      if (gotEvents || executionStarted) {
        setApprovalDecisions((prev) => ({ ...prev, [approvalId]: decision }));
        if (gotEvents) setStreamState('following');
        return;
      }
      console.error('Error submitting approval decision:', error);
      setApprovalDecisions((prev) => ({ ...prev, [approvalId]: 'error' }));
      // Retry repeats THIS decision — same approval, same verdict — not the
      // last chat send. The guards at the top of decideApprovalDecision hold
      // for the repeat: the in-flight mark is gone (finally) and 'error' is
      // not terminal. Only while the chat it belongs to is still the one on
      // screen: after a switch the repeat would post the old chat's ids and
      // stream its continuation into the new one.
      const sessionKey = sessionKeyRef.current;
      cfg.notify?.('Could not submit your approval decision.', {
        severity: 'error',
        onRetry: () => {
          if (sessionKeyRef.current !== sessionKey) return;
          decideRef.current?.(approval, decision);
        },
      });
    } finally {
      resumeAbortsRef.current.delete(controller);
      inflightApprovalsRef.current.delete(approvalId);
    }
  }, [companyName, agentId, captureApproval]);
  decideRef.current = decideApprovalDecision;

  const isOwnSend = useCallback(
    (clientMessageId) => !!clientMessageId && ownSendIdsRef.current.has(clientMessageId),
    [],
  );

  const renameChat = useCallback(async (newName) => {
    if (!companyName || !agentId || !newName?.trim()) return;
    try {
      await setDoc(
        doc(db, 'users', companyName, 'agents', agentId),
        { chat_name: newName.trim() },
        { merge: true }
      );
    } catch (error) {
      // The rename dialog awaits this and shows the failure inline.
      console.error('Error renaming chat:', error);
      throw error;
    }
  }, [companyName, agentId]);

  // Whether canvas edits are on their way to the agent. A queued message holds
  // their export until the running turn ends; a message that has been sent
  // holds it until the ledger shows that message saved, which is also when the
  // page stops counting the edits as unsent. A send that fails clears its
  // pending message, and the edits are then truly unsent again.
  const queuedMessageHoldsCanvasEdit = queuedSend != null && queuedSend.payload.canvasEdit != null;
  const sentMessageHoldsCanvasEdit = pendingUser != null
    && pendingUser.parts.some((part) => part.kind === 'canvas_edit');

  return {
    messages,
    hasOlderMessages,
    loadingOlder,
    loadOlderMessages,
    loadError,
    retryListeners,
    chatName: assistantMeta.chatName,
    chatExists: assistantMeta.exists === true,
    contextPct,
    canvasPushes,
    turnActive,
    releaseWindow,
    streamState,
    queuedSend: queuedSend != null,
    canvasEditInFlight: queuedMessageHoldsCanvasEdit || sentMessageHoldsCanvasEdit,
    // The staged message rendered greyed-out at the end of the thread until
    // the queued send actually fires (or expires back into the composer).
    queuedPreview: queuedSend
      ? {
          text: queuedSend.payload.text || '',
          fileNames: (queuedSend.payload.files || []).map((file) => file.name),
        }
      : null,
    canStop,
    stopping,
    sendTurn,
    stopTurn,
    retryLastSend,
    isOwnSend,
    renameChat,
  };
}
